import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import { useGameStore } from "./store/gameStore";
import { ConnectionPill } from "./components/layout/ConnectionPill";
import { ErrorToast } from "./components/layout/ErrorToast";
import { connect, disconnect, getSocket, wsActions } from "./lib/ws";
import { ensureFreshToken } from "./lib/api";
import { AuthScreen } from "./screens/AuthScreen";
import { LobbyBrowserScreen } from "./screens/LobbyBrowserScreen";
import { WaitingRoomScreen } from "./screens/WaitingRoomScreen";
import { GameScreen } from "./screens/GameScreen";
import { ResetPasswordScreen } from "./screens/ResetPasswordScreen";

function Header() {
  const username = useAuthStore((s) => s.username);
  const clear = useAuthStore((s) => s.clear);
  const reset = useGameStore((s) => s.reset);
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-30 border-b border-line bg-bg/80 backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4">
        <div className="flex items-center gap-2.5">
          <span className="text-brass text-lg">♦</span>
          <span className="font-display text-lg tracking-tight text-ink">Bisca</span>
        </div>
        <div className="flex items-center gap-3">
          <ConnectionPill />
          {username && (
            <span className="hidden text-sm text-ink-dim sm:inline">{username}</span>
          )}
          {username && (
            <button
              className="btn-ghost px-3 py-1.5 text-xs"
              onClick={() => {
                // Leave the lobby over the WS BEFORE closing the socket, so
                // the other players see us go (a mid-game leave just marks us
                // disconnected; a lobby leave removes us and transfers
                // ownership). Without this the socket stayed connected and
                // we haunted the lobby (F6 fix).
                const lobbyId = useGameStore.getState().lobbyId;
                if (lobbyId) wsActions.leave(lobbyId);
                disconnect();
                clear();
                reset();
                navigate("/");
              }}
            >
              Esci
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <footer className="border-t border-line/60 py-4 text-center text-xs text-ink-faint">
        Bisca
      </footer>
      <ErrorToast />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  const bootStatus = useAuthStore((s) => s.bootStatus);
  const loc = useLocation();
  // While the boot restore is in flight (F5 with a persisted refresh token)
  // we must not bounce to login yet — the session may still come back.
  if (bootStatus !== "ready") {
    return <div className="flex h-[60vh] items-center justify-center text-ink-dim">Ripristino sessione…</div>;
  }
  if (!token) return <Navigate to="/" replace state={{ from: loc.pathname }} />;
  return <>{children}</>;
}

export default function App() {
  const kicked = useGameStore((s) => s.kicked);
  const token = useAuthStore((s) => s.accessToken);
  const navigate = useNavigate();

  // Boot restore (F5 / first load): the refresh token survives the reload in
  // sessionStorage but the access token does not. Mint a fresh pair BEFORE
  // the guards decide; a rejected refresh clears the session and RequireAuth
  // falls back to login (with `from` for the post-login redirect).
  useEffect(() => {
    // One-shot cleanup of the old localStorage-based auth persist (identity
    // only), superseded by the per-tab sessionStorage store.
    try {
      localStorage.removeItem("ohre-auth");
    } catch {
      /* storage unavailable — ignore */
    }
    const { accessToken, refreshToken } = useAuthStore.getState();
    if (accessToken) {
      useAuthStore.getState().markReady();
      return;
    }
    if (refreshToken) {
      void ensureFreshToken().finally(() => useAuthStore.getState().markReady());
      return;
    }
    useAuthStore.getState().markReady();
  }, []);

  // WS lifecycle is session-wide, not screen-scoped: the socket is created as
  // soon as we have a session (so the header pill tells the truth on every
  // screen, including the lobby browser), and torn down on logout. Token
  // rotation must NOT rebuild it — ws.ts reads the token dynamically at each
  // (re)connect, and a rebuilt socket would silently drop the lobby attach.
  useEffect(() => {
    if (token && !getSocket()) connect();
    if (!token) disconnect();
  }, [token]);

  useEffect(() => {
    if (kicked) {
      alert(kicked.reason);
      useGameStore.getState().reset();
      navigate("/lobby");
    }
  }, [kicked, navigate]);

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<AuthScreen />} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="/lobby" element={<RequireAuth><LobbyBrowserScreen /></RequireAuth>} />
        <Route path="/lobby/:code" element={<RequireAuth><WaitingRoomScreen /></RequireAuth>} />
        <Route path="/game/:code" element={<RequireAuth><GameScreen /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
